import React from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { Badge } from 'afl-design-system';

import { useSingleSiteSetting } from 'common/react/hooks/use-single-site-setting';
import {
    getStatusContent,
    getStatusVariant
} from 'common/react/utils/status-badge-utils';
import MatchCardClock from './MatchCardClock';

/**
 * Maps a match card's state to the DS `Badge` variant + label shown in the
 * status slot, per the "Tiny Match Card" state -> Badge table.
 *
 * @param {object} props - Props.
 * @param {object} props.match - The normalised match object from useMatchCardData.
 * @param {string} props.genericStatus - One of PULSE.app.common.CONSTANTS.MATCH_STATUS.
 * @param {boolean} props.isPlaceholder - True when either team is a placeholder (unknown teams).
 * @param {string} props.matchId - The match's providerId, used to read the live clock string.
 * @param {object} [props.scoreUpdate] - { show, team: 'home'|'away', type } from useScoreUpdate.
 * @param {object} [props.periodEvent] - { show, period, phase } from usePeriodEvent.
 * @param {boolean} [props.compact] - Mobile tile mode: pre-game shows just the time
 * ("7:10pm"), with the day/date rendered separately by the caller (see formatMatchDayLabel).
 * @returns {React.ReactElement} The status Badge.
 */
export const StatusBadge = ({
    match,
    genericStatus,
    matchId,
    scoreUpdate,
    periodEvent,
    compact = false
}) => {
    const timeString = useSelector((state) =>
        PULSE.app.redux.selectors.match.timeString(state, matchId)
    );
    const selectedRegion = useSingleSiteSetting('region');

    const { label, clock, className, style } = getStatusContent({
        match,
        genericStatus,
        scoreUpdate,
        periodEvent,
        timeString,
        timezone: selectedRegion?.timezone,
        compact
    });

    return (
        <Badge
            size="small"
            variant={getStatusVariant({
                genericStatus,
                scoreUpdate
            })}
            className={className}
            style={style}
        >
            {clock ? <MatchCardClock {...clock} /> : label}
        </Badge>
    );
};

export const SCORE_UPDATE_SHAPE = PropTypes.shape({
    show: PropTypes.bool,
    team: PropTypes.oneOf(['home', 'away']),
    teamAbbr: PropTypes.string,
    type: PropTypes.string
});

export const PERIOD_EVENT_SHAPE = PropTypes.shape({
    show: PropTypes.bool,
    period: PropTypes.object,
    phase: PropTypes.oneOf(['start', 'end'])
});

StatusBadge.propTypes = {
    match: PropTypes.object.isRequired,
    genericStatus: PropTypes.string.isRequired,
    isPlaceholder: PropTypes.bool,
    matchId: PropTypes.string.isRequired,
    scoreUpdate: SCORE_UPDATE_SHAPE,
    periodEvent: PERIOD_EVENT_SHAPE,
    compact: PropTypes.bool
};
